import { useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { TextCellWithTooltip } from "components/DataGridCell";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import {
  ProductsWithMeasurementsQuery,
  ProductsWithMeasurementsQueryVariables,
} from "generated-graphql/graphql";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatChemicalName } from "util/chemicalName";

import { useFacility } from "../Facility/useFacility";
import { FacilityProductMeasurementForm } from "./FacilityProductMeasurementForm";
import { DeleteProductMessage } from "./FacilityProductsTable";
import { ProductsTableProps } from "./ProductsTable";
import { useIgnoredIssues } from "./Report/useReport";
export const PRODUCTS_WITH_MEASUREMENTS = gql(`
  query ProductsWithMeasurements($facilityId: ID!, $reportingYear: Int!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    productMeasurementsForReport(facilityId: $facilityId, reportingYear: $reportingYear, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        totalMeasurements
        productChemicals {
          chemical {
            name
            ...ChemicalPicker
          }
          maxAmount {
            amount
            unit
          }
       }
       issues {
         ...issue
       }
      }
      count
    }
  }
`);

const DELETE_FACILITY_PRODUCT_MEASUREMENTS_BY_PRODUCT_AND_YEAR = gql(`
  mutation DeleteFacilityProductMeasurementsByProductAndYear($facilityId: ID!, $productId: ID!, $reportingYear: Int!) {
    deleteFacilityProductMeasurementsByProductAndYear(facilityId: $facilityId, productId: $productId, reportingYear: $reportingYear) {
      facilityId
      productId
      reportingYear
    }
  }
`);

type ProductsWithMeasurementsTableProps = ProductsTableProps & {
  usesSearchParams?: boolean;
};

export function ProductsWithMeasurementsTable({
  facilityId,
  showReportingYearPicker,
}: ProductsWithMeasurementsTableProps) {
  const ignoredIssues = useIgnoredIssues();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const reportingYear = useSelectedReportingYear();
  const { navigateWithFilters } = useQueryParams<{
    ry: string;
    facilityId: string;
    omnisearch: string;
  }>();

  const { data: facilityQuery } = useFacility(facilityId);

  const [showAddMeasurementForm, setShowAddMeasurementForm] = useState(false);
  const [deletingProductMeasurements, setDeletingProductMeasurements] = useState<
    ProductsWithMeasurementsQuery["productMeasurementsForReport"]["items"][number] | undefined
  >(undefined);

  const location = useLocation();
  const usedInInventory = location.pathname.includes("inventory");

  const navigateToProductMeasurements = useCallback(
    (productId: string) => {
      if (usedInInventory) {
        navigateWithFilters(productId, (filters) => {
          filters.facilityId = facilityId;
          filters.ry = String(reportingYear);
          // The inventory list's search is page-specific and must not follow the user
          // into the product's Measurements page, where it would filter that grid.
          delete filters.omnisearch;
        });
        return;
      }

      navigate(productId);
    },
    [navigate, navigateWithFilters, usedInInventory, facilityId, reportingYear]
  );

  const [deleteFacilityProductMeasurementsByProductAndYear, { loading: isDeleting }] = useMutation(
    DELETE_FACILITY_PRODUCT_MEASUREMENTS_BY_PRODUCT_AND_YEAR,
    {
      onCompleted() {
        alerts.success("Successfully deleted the measurements");
        setDeletingProductMeasurements(undefined);
      },
      onError(err) {
        alerts.error("An error occurred deleting the measurements", err);
        setDeletingProductMeasurements(undefined);
      },
      refetchQueries: [PRODUCTS_WITH_MEASUREMENTS],
    }
  );

  const handleDelete = useCallback(() => {
    if (!deletingProductMeasurements) return;
    deleteFacilityProductMeasurementsByProductAndYear({
      variables: {
        facilityId,
        productId: deletingProductMeasurements.id,
        reportingYear,
      },
    });
  }, [
    deleteFacilityProductMeasurementsByProductAndYear,
    deletingProductMeasurements,
    facilityId,
    reportingYear,
  ]);

  return (
    <>
      <OmnisearchDataGrid<
        ProductsWithMeasurementsQuery,
        ProductsWithMeasurementsQueryVariables,
        ProductsWithMeasurementsQuery["productMeasurementsForReport"]["items"][number]
      >
        persistenceKey="products-with-measurements"
        isURLDriven
        columns={[
          {
            headerName: "Product",
            field: "name",
            flex: 2,
            topAligned: true,
            renderCell: ({ row }) => <TextCellWithTooltip>{row.name}</TextCellWithTooltip>,
          },
          {
            headerName: "Measurements",
            field: "facilityProductMeasurementCount",
            flex: 1,
            topAligned: true,
            renderCell: ({ row }) => (
              <TextCellWithTooltip>{row.totalMeasurements}</TextCellWithTooltip>
            ),
          },
          {
            headerName: "Chemicals in Product",
            field: "chemicalsInProduct",
            flex: 2,
            sortable: false,
            topAligned: true,
            renderCell({ row }) {
              if (row.productChemicals?.length) {
                return (
                  <Box style={{ width: "100%" }}>
                    {row.productChemicals.map(({ chemical }) => (
                      <TextCellWithTooltip key={chemical.id} tooltip={formatChemicalName(chemical)}>
                        {chemical.name}
                      </TextCellWithTooltip>
                    ))}
                  </Box>
                );
              }
            },
          },
          {
            headerName: "Max Amount",
            field: "maxAmount",
            flex: 1,
            sortable: false,
            topAligned: true,
            renderCell({ row }) {
              if (row.productChemicals?.length) {
                return (
                  <Box style={{ width: "100%" }}>
                    {row.productChemicals.map(({ chemical, maxAmount }) => (
                      <TextCellWithTooltip key={chemical.id}>
                        {prettyPrintQuantity({
                          amount: maxAmount?.amount,
                          unit: maxAmount?.unit,
                          state: facilityQuery?.facility?.state,
                        })}
                      </TextCellWithTooltip>
                    ))}
                  </Box>
                );
              }
            },
          },
          {
            headerName: "Issues",
            field: "issueCount",
            align: "center",
            headerAlign: "center",
            renderCell: ({ row }) => {
              const shouldIgnore = ignoredIssues.some((issue) =>
                row.issues?.some((i) => i.modelId === issue.modelId)
              );
              return <IssueCount issueCount={row.issues?.length} ignoreForReport={shouldIgnore} />;
            },
            sortable: false,
            flex: 0.8,
          },
          {
            field: "actions",
            type: "actions",
            getActions({ row }) {
              return [
                <GridActionsCellItem
                  key="edit"
                  label="Edit"
                  icon={<Edit />}
                  onClick={() => navigateToProductMeasurements(row.id)}
                />,
                <GridActionsCellItem
                  key="delete"
                  label="Delete"
                  icon={<Delete />}
                  onClick={() => setDeletingProductMeasurements(row)}
                />,
              ];
            },
          },
        ]}
        dataQuery={PRODUCTS_WITH_MEASUREMENTS}
        dataQueryVariables={{
          reportingYear,
          facilityId,
        }}
        defaultSearch={`facilityId:${facilityId} reportingYear:${reportingYear}`}
        getItems={(data) => data.productMeasurementsForReport.items}
        getCount={(data) => data.productMeasurementsForReport.count}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        noDataMessage="No products found"
        noDataButtonText="Add Product"
        rowHeight={"auto"}
        noDataOnButtonClick={() => setShowAddMeasurementForm(true)}
        excludeFilterColumns={[
          "issueCount",
          "maxAmount",
          "chemicalsInProduct",
          "facilityProductMeasurementCount",
        ]}
        additionalFilterColumns={[{ key: "chemical", header: "Chemical" }]}
        commandButtons={[
          showReportingYearPicker ? (
            <TierIIYearPicker
              key="yearPicker"
              selectProps={{
                size: "small",
              }}
              persistToLocalStorage
            />
          ) : undefined,
          <CommandButton
            key="add"
            variant="contained"
            onClick={() => setShowAddMeasurementForm(true)}
          >
            Add Product
          </CommandButton>,
        ]}
        withPadding={false}
        onRowClick={({ row }) => navigateToProductMeasurements(row.id)}
      />
      {!!deletingProductMeasurements && (
        <ConfirmDialog
          open={!!deletingProductMeasurements}
          onClose={() => setDeletingProductMeasurements(undefined)}
          onConfirm={handleDelete}
          header="Are you sure you want to delete this product measurement?"
          resources={[
            {
              resourceType: "Product Measurement",
              resourceName: deletingProductMeasurements.name,
            },
            {
              resourceType: "Reporting Year",
              resourceName: reportingYear,
            },
          ]}
          info={DeleteProductMessage({
            productName: deletingProductMeasurements.name,
            forMeasurements: true,
          })}
          loading={isDeleting}
        />
      )}
      {showAddMeasurementForm && (
        <FacilityProductMeasurementForm
          facilityId={facilityId}
          open={showAddMeasurementForm}
          onClose={() => setShowAddMeasurementForm(false)}
        />
      )}
    </>
  );
}
